import React, { useEffect } from 'react';
import SimpleMarkdown from '@khanacademy/simple-markdown';
import styled from 'styled-components';

import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Box, Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import { BannerSeverity } from '@rover/kibble/official-patterns/Banner/BannerSeverity.constants';
import IconBlock from '@rover/kibble/patterns/IconBlock';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { getPath } from '@rover/shared/js/utils/getFullUrl';

import {
  SearchPageNoDatesHeaderShownEvent,
  SearchPageYesDatesHeaderShownEvent,
} from './analyticsEvents';
import {
  buildTestId,
  getAlertElement,
  getIcon,
  SearchSectionHeaderProps,
} from './SearchSectionHeader.common';

const StyledInfoIcon = styled(AlertInfo)`
  fill: ${({ theme }) => theme.colors.interactive_link.primary_base};
`;

export const getTextElement = (text?: string): React.ReactNode => {
  if (!text) {
    return null;
  }
  return SimpleMarkdown.defaultReactOutput(SimpleMarkdown.defaultInlineParse(text));
};

const SearchSectionHeader = ({
  id = '',
  headerIconName,
  headerText,
  infoIconOnClick,
  primarySubheaderText,
  secondarySubheaderText,
  alertText,
  alertSeverity,
  alertLinkHref,
  alertLinkEvent,
}: SearchSectionHeaderProps): JSX.Element => {
  const theme = useTheme();
  useEffect(() => {
    const path = getPath();
    if (path === '/search/') {
      if (alertSeverity === BannerSeverity.WARNING) {
        emitAnalyticsEvent(new SearchPageNoDatesHeaderShownEvent());
      } else {
        emitAnalyticsEvent(new SearchPageYesDatesHeaderShownEvent());
      }
    }
  }, [alertSeverity]);

  const headerIcon = getIcon(headerIconName);
  const primarySubheaderTextElement = getTextElement(primarySubheaderText);
  const secondarySubheaderTextElement = getTextElement(secondarySubheaderText);
  const alertElement = getAlertElement(alertText, alertLinkHref, alertLinkEvent, getTextElement);

  return (
    <Box
      data-testid={buildTestId(id, headerIconName)}
      pt="6x"
      px="4x"
      pb="6x"
      sx={{
        backgroundColor: theme.background.primary,
        borderBottom: `1px solid ${theme.colors.border.primary}`,
      }}
    >
      <Flex flexDirection="row" alignItems="center" justifyContent="space-between">
        <IconBlock title={headerText} icon={headerIcon} iconSize="23px" iconMargin="2x" />
        {infoIconOnClick && (
          <Button
            onClick={infoIconOnClick}
            variant="flat"
            size="small"
            padding="2x"
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
            aria-label="Info"
            icon={StyledInfoIcon}
          />
        )}
      </Flex>
      {primarySubheaderTextElement && (
        <Paragraph size="100" textColor="tertiary" pt="2x" mb="0x">
          {primarySubheaderTextElement}
        </Paragraph>
      )}
      {secondarySubheaderTextElement && (
        <Paragraph size="100" textColor="tertiary" pt="2x" mb="0x">
          {secondarySubheaderTextElement}
        </Paragraph>
      )}
      {alertText &&
        (alertSeverity ? (
          <Banner
            severity={alertSeverity}
            testID={`banner-alert-${alertSeverity}`}
            hideIcon
            suppressDefaultTypography
            containerProps={{ mt: '2x' }}
          >
            <Text>{alertElement}</Text>
          </Banner>
        ) : (
          alertElement
        ))}
    </Box>
  );
};

export default SearchSectionHeader;
